/**
 * Voice Service for Luna AI Assistant
 * Handles child-friendly female voice selection and TTS optimization
 * Designed specifically for kids aged 8-14 years old
 */

import toast from './toastService'

class VoiceService {
  constructor() {
    this.speechSynthesis = null
    this.availableVoices = []
    this.selectedVoice = null
    this.voicePreferences = this.loadVoicePreferences()
    
    // Child-friendly voice parameters
    this.childFriendlySettings = {
      pitch: 1.3,           // Higher pitch for youthful sound (1.2-1.4 range)
      rate: 0.95,           // Slightly slower for clarity (0.9-1.1 range)  
      volume: 0.8           // Comfortable volume level
    }
    
    this.initialize()
  }

  /**
   * Initialize voice service and load available voices
   */
  async initialize() {
    if ('speechSynthesis' in window) {
      this.speechSynthesis = window.speechSynthesis
      
      // Load voices (may take a moment on some browsers)
      await this.loadVoices()
      
      // Select best child-friendly voice
      this.selectBestChildFriendlyVoice()
    } else {
      console.warn('Speech synthesis not supported in this browser')
      toast.error('Voice features not supported in this browser')
    }
  }

  /**
   * Load available voices from browser
   */
  async loadVoices() {
    return new Promise((resolve) => {
      // Voices might not be immediately available
      const loadVoicesAttempt = () => {
        this.availableVoices = this.speechSynthesis.getVoices()
        
        if (this.availableVoices.length > 0) {
          
          resolve(this.availableVoices)
        } else {
          // Try again in a moment
          setTimeout(loadVoicesAttempt, 100)
        }
      }
      
      // Listen for voice changes (some browsers load voices asynchronously)
      this.speechSynthesis.onvoiceschanged = loadVoicesAttempt
      
      // Immediate attempt
      loadVoicesAttempt()
    })
  }

  /**
   * Filter and select the best child-friendly female voice
   */
  selectBestChildFriendlyVoice() {
    if (this.availableVoices.length === 0) {
      
      return null
    }

    // Step 1: Filter for English voices
    const englishVoices = this.availableVoices.filter(voice => 
      voice.lang.startsWith('en')
    )

    // Step 2: Filter for female voices (various naming patterns)
    const femaleVoices = englishVoices.filter(voice => {
      const name = voice.name.toLowerCase()
      return (
        name.includes('female') ||
        name.includes('woman') ||
        name.includes('girl') ||
        name.includes('samantha') ||
        name.includes('karen') ||
        name.includes('zira') ||
        name.includes('hazel') ||
        name.includes('susan') ||
        name.includes('allison') ||
        name.includes('aria') ||
        // Check if it's explicitly not male
        (!name.includes('male') && !name.includes('man') && !name.includes('boy'))
      )
    })

    // Step 3: Prioritize child-friendly voices
    const childFriendlyVoices = femaleVoices.sort((a, b) => {
      const aName = a.name.toLowerCase()
      const bName = b.name.toLowerCase()
      
      // Higher priority for child/young voices
      if (aName.includes('child') || aName.includes('young') || aName.includes('girl')) return -1
      if (bName.includes('child') || bName.includes('young') || bName.includes('girl')) return 1
      
      // Prefer natural/neural voices over robotic ones
      if (aName.includes('neural') || aName.includes('natural')) return -1
      if (bName.includes('neural') || bName.includes('natural')) return 1
      
      // Prefer voices with quality indicators
      if (aName.includes('premium') || aName.includes('enhanced')) return -1
      if (bName.includes('premium') || bName.includes('enhanced')) return 1
      
      return 0
    })

    // Step 4: Select the best voice (with fallback)
    const bestVoice = this.selectFromPreferences(childFriendlyVoices) || 
                     childFriendlyVoices[0] || 
                     femaleVoices[0] || 
                     englishVoices[0] || 
                     this.availableVoices[0]

    this.selectedVoice = bestVoice
    
    if (bestVoice) {
      console.log(`Selected child-friendly voice: ${bestVoice.name} (${bestVoice.lang})`)
      this.saveVoicePreferences(bestVoice)
    }
    
    return bestVoice
  }

  /**
   * Select voice based on user preferences
   */
  selectFromPreferences(voices) {
    if (!this.voicePreferences.selectedVoiceName) return null
    
    return voices.find(voice => 
      voice.name === this.voicePreferences.selectedVoiceName
    )
  }

  /**
   * Get all child-friendly female voices for user selection
   */
  getChildFriendlyVoices() {
    const englishVoices = this.availableVoices.filter(voice => 
      voice.lang.startsWith('en')
    )

    const femaleVoices = englishVoices.filter(voice => {
      const name = voice.name.toLowerCase()
      return (
        name.includes('female') ||
        name.includes('woman') ||
        name.includes('girl') ||
        !name.includes('male')
      )
    })

    return femaleVoices.map(voice => ({
      name: voice.name,
      lang: voice.lang,
      localService: voice.localService,
      isChildFriendly: this.isChildFriendlyVoice(voice),
      voice: voice
    }))
  }

  /**
   * Check if a voice is particularly child-friendly
   */
  isChildFriendlyVoice(voice) {
    const name = voice.name.toLowerCase()
    return (
      name.includes('child') ||
      name.includes('young') ||
      name.includes('girl') ||
      name.includes('neural') ||
      name.includes('natural')
    )
  }

  /**
   * Speak text with Luna's child-friendly voice
   */
  async speakAsLuna(text, options = {}) {
    if (!this.speechSynthesis || !this.selectedVoice) {
      
      return false
    }

    // Stop any current speech
    this.speechSynthesis.cancel()

    const utterance = new SpeechSynthesisUtterance(text)
    
    // Apply child-friendly settings
    utterance.voice = this.selectedVoice
    utterance.pitch = options.pitch || this.childFriendlySettings.pitch
    utterance.rate = options.rate || this.childFriendlySettings.rate
    utterance.volume = options.volume || this.childFriendlySettings.volume

    // Add event listeners
    return new Promise((resolve, reject) => {
      utterance.onend = () => resolve(true)
      utterance.onerror = (error) => {
        console.error('Speech synthesis error:', error)
        reject(error)
      }
      
      this.speechSynthesis.speak(utterance)
    })
  }

  /**
   * Preview a voice with sample text
   */
  async previewVoice(voice, sampleText = "Hi! I'm Luna, your friendly learning assistant!") {
    if (!this.speechSynthesis) return false

    this.speechSynthesis.cancel()

    const utterance = new SpeechSynthesisUtterance(sampleText)
    utterance.voice = voice
    utterance.pitch = this.childFriendlySettings.pitch
    utterance.rate = this.childFriendlySettings.rate
    utterance.volume = this.childFriendlySettings.volume

    return new Promise((resolve) => {
      utterance.onend = () => resolve(true)
      utterance.onerror = () => resolve(false)
      
      this.speechSynthesis.speak(utterance)
    })
  }

  /**
   * Stop current speech
   */
  stopSpeaking() {
    if (this.speechSynthesis) {
      this.speechSynthesis.cancel()
    }
  }

  /**
   * Update voice preferences
   */
  setVoicePreferences(voiceName, settings = {}) {
    this.voicePreferences.selectedVoiceName = voiceName
    
    if (settings.pitch) this.childFriendlySettings.pitch = settings.pitch
    if (settings.rate) this.childFriendlySettings.rate = settings.rate
    if (settings.volume) this.childFriendlySettings.volume = settings.volume
    
    this.saveVoicePreferences()
    
    // Update selected voice
    const newVoice = this.availableVoices.find(voice => voice.name === voiceName)
    if (newVoice) {
      this.selectedVoice = newVoice
    }
  }

  /**
   * Save voice preferences to localStorage
   */
  saveVoicePreferences(voice = null) {
    const preferences = {
      selectedVoiceName: voice ? voice.name : this.voicePreferences.selectedVoiceName,
      settings: this.childFriendlySettings,
      lastUpdated: new Date().toISOString()
    }
    
    localStorage.setItem('lunaVoicePreferences', JSON.stringify(preferences))
    this.voicePreferences = preferences
  }

  /**
   * Load voice preferences from localStorage
   */
  loadVoicePreferences() {
    try {
      const stored = localStorage.getItem('lunaVoicePreferences')
      if (stored) {
        const preferences = JSON.parse(stored)
        
        // Merge with defaults
        if (preferences.settings) {
          this.childFriendlySettings = {
            ...this.childFriendlySettings,
            ...preferences.settings
          }
        }
        
        return preferences
      }
    } catch (error) {
      console.warn('Failed to load voice preferences:', error)
    }
    
    return {
      selectedVoiceName: null,
      settings: this.childFriendlySettings,
      lastUpdated: null
    }
  }

  /**
   * Get current voice information
   */
  getCurrentVoiceInfo() {
    return {
      selectedVoice: this.selectedVoice,
      settings: this.childFriendlySettings,
      availableVoices: this.getChildFriendlyVoices(),
      isInitialized: !!this.speechSynthesis
    }
  }
}

// Export singleton instance
export default new VoiceService() 